import {
  KG_PER_LB,
  LITERS_PER_US_GALLON,
  roundNumber as roundNumberCentral,
  formatNumberInput as formatNumberInputCentral,
} from "../../utils/fuelFormat";

const padUtcPart = (value: number) => `${value}`.padStart(2, "0");

export const LEON_UTC_INPUT_FORMAT = "YYYY-MM-DD HH:mm";

export const formatUtcDateTimeInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";

  return [
    `${date.getUTCFullYear()}-${padUtcPart(date.getUTCMonth() + 1)}-${padUtcPart(date.getUTCDate())}`,
    `${padUtcPart(date.getUTCHours())}:${padUtcPart(date.getUTCMinutes())}`,
  ].join(" ");
};

export const parseUtcDateTimeInput = (value?: string | null) => {
  if (!value) return null;
  const normalized = `${value}`.trim();
  if (!normalized) return null;

  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/,
  );
  if (!match) return null;

  const [, yearRaw, monthRaw, dayRaw, hourRaw, minuteRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    !Number.isInteger(hour) ||
    !Number.isInteger(minute)
  ) {
    return null;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  if (Number.isNaN(parsed.valueOf())) return null;

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day ||
    parsed.getUTCHours() !== hour ||
    parsed.getUTCMinutes() !== minute
  ) {
    return null;
  }

  return parsed.toISOString();
};

export const calculateDurationMinutes = (
  startValue?: string | null,
  endValue?: string | null,
) => {
  if (!startValue || !endValue) return null;

  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf())) {
    return null;
  }

  const diffMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (!Number.isFinite(diffMinutes) || diffMinutes < 0) {
    return null;
  }

  return diffMinutes;
};

export const formatDurationMinutes = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) {
    return "--:--";
  }

  const totalMinutes = Math.round(value);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${padUtcPart(hours)}:${padUtcPart(minutes)}`;
};

export const buildLeonPickerOptions = (
  configuredOptions: Array<string | null | undefined> = [],
  currentValues: Array<string | null | undefined> = [],
) => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  const push = (value?: string | null) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    normalized.push(trimmed);
  };

  configuredOptions.forEach(push);
  currentValues.forEach(push);

  return normalized;
};

export const parseNumberInput = (value?: string | null) => {
  if (value === null || value === undefined) return null;
  const trimmed = `${value}`.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const roundNumber = (value: number, digits = 2) => {
  const rounded = roundNumberCentral(value, digits);
  return rounded === null ? Number.NaN : rounded;
};

export const formatNumberInput = (value: number | null, digits = 2) =>
  formatNumberInputCentral(value, digits);

export const normalizeDensityToKgPerL = (
  value: string | number | null | undefined,
  unit?: string | null,
) => {
  const density = typeof value === "number" ? value : parseNumberInput(value);
  if (density === null) return null;

  const normalizedUnit = `${unit || ""}`
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

  if (!normalizedUnit) return density;
  if (
    [
      "kg/l",
      "kg/lt",
      "kg/ltr",
      "kg/liter",
      "kg/litre",
      "kgperlt",
      "kgperliter",
      "kgperlitre",
      "kg/liter",
      "kg/litre",
    ].includes(normalizedUnit)
  ) {
    return density;
  }
  if (normalizedUnit === "kg/m3" || normalizedUnit === "kg/m³") {
    return density / 1000;
  }
  if (normalizedUnit === "g/cm3" || normalizedUnit === "g/cm³") {
    return density;
  }
  if (
    [
      "lb/gal",
      "lbs/gal",
      "lb/usgal",
      "lbs/usgal",
      "lbspergal",
      "lbpergal",
      "lbsperusgal",
      "lbperusgal",
    ].includes(normalizedUnit)
  ) {
    return (density * KG_PER_LB) / LITERS_PER_US_GALLON;
  }
  if (normalizedUnit === "lb/l" || normalizedUnit === "lbs/l") {
    return density * KG_PER_LB;
  }
  if (normalizedUnit === "kg/hl") {
    return density / 100;
  }

  return density;
};

export const convertUpliftVolumeToMass = ({
  liters,
  density,
  densityUnit,
  massFuelUnit,
}: {
  liters: string | number | null | undefined;
  density: string | number | null | undefined;
  densityUnit?: string | null;
  massFuelUnit: string;
}) => {
  const volume = typeof liters === "number" ? liters : parseNumberInput(liters);
  const densityKgPerL = normalizeDensityToKgPerL(density, densityUnit);
  if (volume === null || densityKgPerL === null || densityKgPerL <= 0) return null;

  const massKg = volume * densityKgPerL;
  return `${massFuelUnit}`.toLowerCase() === "kg"
    ? Math.round(massKg)
    : roundNumber(massKg / KG_PER_LB, 2);
};

export const convertUpliftMassToVolume = ({
  mass,
  density,
  densityUnit,
  massFuelUnit,
}: {
  mass: string | number | null | undefined;
  density: string | number | null | undefined;
  densityUnit?: string | null;
  massFuelUnit: string;
}) => {
  const massValue = typeof mass === "number" ? mass : parseNumberInput(mass);
  const densityKgPerL = normalizeDensityToKgPerL(density, densityUnit);
  if (massValue === null || densityKgPerL === null || densityKgPerL <= 0) return null;

  const massKg = `${massFuelUnit}`.toLowerCase() === "kg"
    ? massValue
    : massValue * KG_PER_LB;
  return roundNumber(massKg / densityKgPerL, 2);
};
